import {
    getCommentAnchorPart,
    getShareLinkCommentPartLabel,
    getShareLinkCommentTaskPartLabel,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-parts"

export const SHARE_LINK_COMMENTS_TAB = "Comments"
export const SHARE_LINK_COMMENT_TARGET_REFERENCE = "ShareLinkCommentTarget"
export const SHARE_LINK_COMMENT_SHARE_PARAM = "commentShare"

export function filterShareLinkCommentThreads(comments = [], viewer = {}) {
    if (!viewer.isShareLinkUser) return comments

    return comments.filter(isVisibleThread).map(filterVisibleReplies)

    function isVisibleThread(comment) {
        return isOwnComment(comment)
    }

    function filterVisibleReplies(comment) {
        return {
            ...comment,
            replies: (comment.replies ?? []).filter(isVisibleReply),
        }
    }

    function isVisibleReply(reply) {
        return isInternalComment(reply) || isOwnComment(reply)
    }

    function isOwnComment(comment) {
        return !!viewer.userId && comment.authorUserId === viewer.userId
    }
}

export function canViewerReplyToComment(comment, viewer = {}) {
    if (!viewer.isShareLinkUser) return true
    return !!viewer.userId && comment?.authorUserId === viewer.userId
}

export function findSelectedShareLinkComment(comments = [], commentId) {
    if (!commentId) return null
    return comments.find(matchesCommentId) ?? null

    function matchesCommentId(comment) {
        return comment?.id === commentId || comment?.threadId === commentId
    }
}

export function filterShareLinkCommentHighlights(comments = [], { shareLinkId } = {}) {
    // Threads whose every message has been deleted are removed server-side on the last delete, but
    // guard here too so stale cached data can't keep an orphaned highlight alive in the schedule.
    const visible = comments.filter(hasVisibleShareLinkCommentMessage)
    if (!shareLinkId) return visible
    return visible.filter((comment) => !comment.resolved)
}

export function hasVisibleShareLinkCommentMessage(thread) {
    if (!thread) return false
    if (!thread.deleted) return true
    return (thread.replies ?? []).some((reply) => !reply.deleted)
}

export function isShareLinkCommentForTarget(comment, { targetType, targetId, taskIndex } = {}) {
    const anchor = comment?.anchor ?? {}
    if (!anchor.targetId) return false
    if (targetType && anchor.type !== targetType) return false
    if (targetId && anchor.targetId === targetId) return true
    if (taskIndex !== undefined && anchor.taskIndex === taskIndex) return true
    return false
}

export function buildScheduleCommentAnchor({ schedule }) {
    const scheduleVersion = getScheduleVersion(schedule?.version)
    return {
        type: "schedule",
        scheduleId: schedule?.id,
        scheduleVersion,
        targetId: schedule?.id,
        targetLabel: schedule?.title ?? schedule?.code ?? "Schedule",
        targetText: schedule?.title ?? "",
    }
}

export function buildScheduleCommentSelectionMetadata({ anchor, schedule, scheduleId }) {
    return {
        path: ["schedule"],
        parentId: scheduleId ?? schedule?.id,
        start: 0,
        end: 0,
        selectedText: anchor?.targetText ?? schedule?.title ?? "",
        beforeText: "",
        afterText: "",
    }
}

export function buildTaskCommentTarget({ task, selectedText = "", part = null, selectionContext = null }) {
    const taskIndex = getTaskIndex(task)
    const partLabel = getShareLinkCommentPartLabel(part)
    const targetText = getTargetText(selectedText, task?.title, task?.content, "Task")
    return {
        type: "task",
        targetId: task?.id,
        targetLabel: partLabel ? `Task ${taskIndex + 1} · ${partLabel}` : `Task ${taskIndex + 1}`,
        targetText,
        selectionMetadata: {
            // The part rides as a third path segment AND as explicit fields, so consumers that only
            // know the path shape (["tasks", "<index>", "<part>"]) still resolve it.
            path: partLabel ? ["tasks", String(taskIndex), part] : ["tasks", String(taskIndex)],
            parentId: task?.id,
            taskIndex,
            ...(partLabel ? { part, partLabel } : {}),
            start: selectionContext?.start ?? 0,
            end: selectionContext?.end ?? targetText.length,
            selectedText: targetText,
            beforeText: selectionContext?.beforeText ?? "",
            afterText: selectionContext?.afterText ?? "",
        },
    }
}

export function buildIntroCommentTarget({ schedule, selectedText = "", selectionContext = null }) {
    const targetText = getTargetText(selectedText, schedule?.intro?.content, schedule?.title, "Introduction")
    return {
        type: "intro",
        targetId: "intro",
        targetLabel: "Introduction",
        targetText,
        selectionMetadata: {
            path: ["intro", "content"],
            parentId: "intro",
            start: selectionContext?.start ?? 0,
            end: selectionContext?.end ?? targetText.length,
            selectedText: targetText,
            beforeText: selectionContext?.beforeText ?? "",
            afterText: selectionContext?.afterText ?? "",
        },
    }
}

// One icon per anchor type across every comments surface (schedule tab header, regime directory
// chips) so an anchor reads the same wherever it appears.
export function getAnchorIcon(type) {
    if (type === "intro") return "mdi:text-box-outline"
    if (type === "task") return "mdi:wrench-outline"
    return "mdi:file-document-outline"
}

export function resolveCommentAnchorPresentation(comment, schedule) {
    const anchor = comment?.anchor ?? {}
    const hasSchedule = !!schedule
    const scheduleVersion = getScheduleVersion(schedule?.version)
    const commentVersion = Number(anchor.scheduleVersion ?? scheduleVersion)
    const fallbackLabel = getAnchorDisplayLabel(anchor, schedule)
    const targetText = anchor.targetText ?? ""
    // The version-mismatch pill and "original text changed" warning compare the comment against the
    // schedule version being viewed. In aggregated views with no schedule in context (the regime
    // Comments tab) there is nothing to compare against, so these would always misfire — suppress them.
    const isEarlierVersion = hasSchedule && commentVersion < scheduleVersion
    const isDifferentVersion = hasSchedule && commentVersion !== scheduleVersion
    const status = hasSchedule ? getAnchorStatus({ anchor, schedule, targetText, isEarlierVersion }) : "available"

    return {
        commentVersionLabel: `on v${commentVersion}`,
        fallbackLabel,
        partLabel: getShareLinkCommentTaskPartLabel(getCommentAnchorPart(comment)),
        icon: getAnchorIcon(anchor.type),
        isDifferentVersion,
        isEarlierVersion,
        scheduleVersionLabel: `Viewing v${scheduleVersion}`,
        status,
        statusLabel: getStatusLabel(status),
        versionLabel: `on v${commentVersion}`,
    }
}

// A task comment's stored anchor label is just the schedule title, which makes every task comment
// header read the same. Resolve the actual task from the schedule by index so the header names the
// specific task (e.g. "3. Cleaning"); fall back to "Task N", then the non-task anchor label.
function getAnchorDisplayLabel(anchor = {}, schedule) {
    if (anchor.type === "task" && Number.isInteger(anchor.taskIndex)) {
        const task = schedule?.tasks?.[anchor.taskIndex]
        const title = `${task?.title ?? ""}`.trim()
        const number = anchor.taskIndex + 1
        if (title) return `${number}. ${title}`
        return `Task ${number}`
    }
    return anchor.targetLabel ?? schedule?.title ?? "Schedule"
}

export function getShareLinkCommentScheduleRoute({
    schedulePath,
    shareLinkId,
    commentShareLinkId,
    commentId,
    version,
    editEx,
}) {
    const searchParams = new URLSearchParams()
    if (version !== undefined && version !== null) {
        searchParams.set("version", version)
    }
    if (editEx) {
        searchParams.set("editEx", "true")
    }
    if (shareLinkId) {
        searchParams.set("share", shareLinkId)
    }
    if (commentShareLinkId) {
        searchParams.set(SHARE_LINK_COMMENT_SHARE_PARAM, commentShareLinkId)
    }
    searchParams.set("ScheduleRightBarTab", SHARE_LINK_COMMENTS_TAB)
    if (commentId) {
        searchParams.set("comment", commentId)
    }

    return `${getScheduleRouteRoot(editEx)}/${encodeSchedulePath(schedulePath)}?${searchParams.toString()}`
}

export function getShareLinkCommentOpenSchedulePath(comment = {}) {
    return comment.schedulePath ?? comment.scheduleId
}

export function getShareLinkCommentRouteShareLinkId({ currentShareLinkId, shareLinkId } = {}) {
    if (!currentShareLinkId) return undefined
    return shareLinkId
}

export function getShareLinkCommentRouteCommentShareLinkId({ currentShareLinkId, shareLinkId } = {}) {
    if (currentShareLinkId) return undefined
    return shareLinkId
}

export function shouldShowShareLinkCommentsTab({ shareLinkId, shareLink }) {
    if (!shareLinkId) return false
    return !!shareLink?.allowComments
}

function getAnchorStatus({ anchor, schedule, targetText, isEarlierVersion }) {
    if (anchor.status) return anchor.status
    if (!targetText) return "available"
    if (!isEarlierVersion) return "available"
    // Match with all whitespace removed. `stripHtml` turns inline tags (<strong>, <em>, links, …) into
    // spaces, but the stored plain-text selection has none — so a whitespace-sensitive `includes` would
    // falsely flag unchanged text as "changed" whenever the content has inline formatting. Comparing
    // whitespace-insensitively keeps genuine text changes detected while ignoring formatting/whitespace.
    const needle = withoutWhitespace(normalizeSearchText(targetText))
    if (!needle) return "available"
    if (withoutWhitespace(getScheduleSearchText(schedule)).includes(needle)) return "available"
    return "original-text-changed"
}

function withoutWhitespace(value) {
    return `${value}`.replace(/\s+/g, "")
}

function getStatusLabel(status) {
    if (status === "original-text-changed") return "Original text changed"
    if (status === "orphaned") return "Original target unavailable"
    return ""
}

function getScheduleSearchText(schedule) {
    const parts = [schedule?.title, schedule?.intro?.content, schedule?.intro?.note]
    for (const task of schedule?.tasks ?? []) {
        parts.push(task.title, task.content, task.note)
    }
    return normalizeSearchText(parts.filter(Boolean).join(" "))
}

function stripHtml(value) {
    return `${value}`.replace(/<[^>]+>/g, " ")
}

function normalizeSearchText(value) {
    return normalizeText(decodeHtmlEntities(stripHtml(value)))
}

function decodeHtmlEntities(value) {
    return `${value}`
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#(\d+);/g, decodeNumericEntity)
}

function decodeNumericEntity(match, code) {
    return String.fromCodePoint(Number.parseInt(code, 10))
}

function normalizeText(value) {
    return `${value}`.replace(/\s+/g, " ").trim().toLowerCase()
}

function normalizeDisplayText(value) {
    return `${value ?? ""}`
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
}

function getTargetText(selectedText, primaryText, fallbackText, fallbackLabel) {
    const selected = normalizeDisplayText(selectedText)
    if (selected) return selected
    const primary = normalizeDisplayText(primaryText)
    if (primary) return primary
    const fallback = normalizeDisplayText(fallbackText)
    if (fallback) return fallback
    return fallbackLabel
}

function getTaskIndex(task) {
    const parsed = Number.parseInt(task?.$index ?? task?.index ?? 1, 10)
    if (Number.isNaN(parsed) || parsed < 1) return 0
    return parsed - 1
}

function isInternalComment(comment) {
    return comment?.authorType === "internal"
}

function getScheduleVersion(version) {
    const parsed = Number.parseInt(version ?? 1, 10)
    if (Number.isNaN(parsed)) return 1
    return parsed
}

function encodeSchedulePath(schedulePath) {
    return `${schedulePath}`.replace(/\|/g, "%7C")
}

function getScheduleRouteRoot(editEx) {
    if (editEx) return "/app/schedules"
    return "/app/facilities/schedule"
}
